import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/apiClient";
import { Panel, SectionTitle } from "@/components/sov";
import { Globe2, Grid3x3, Server } from "lucide-react";

// Equirectangular projection
const projectGeo = (lat, lon, width, height) => {
  const x = ((lon + 180) / 360) * width;
  const y = ((90 - lat) / 180) * height;
  return { x, y };
};

// MITRE cell color
const mitreCellStyle = (count, critical) => {
  if (count === 0) return { background: "rgba(255,255,255,0.015)", color: "var(--ivory-3)" };
  const intensity = Math.min(1, count / 30);
  const isCritical = critical > 0;
  const base = isCritical ? "200, 68, 59" : "200, 162, 75";
  const alpha = 0.15 + intensity * 0.6;
  return {
    background: `rgba(${base}, ${alpha})`,
    borderColor: `rgba(${base}, ${Math.min(1, 0.4 + intensity)})`,
    color: intensity > 0.4 ? "#fff5d0" : "var(--ivory)",
  };
};

const GeoHeatmap = ({ nodes }) => {
  const W = 900, H = 440;
  const max = Math.max(...nodes.map((n) => n.count), 1);

  return (
    <div className="relative" data-testid="geo-heatmap">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" style={{ background: "linear-gradient(180deg, rgba(7,7,10,0.6), rgba(12,12,17,0.4))" }}>
        {/* lat/lon grid */}
        <g stroke="rgba(200,162,75,0.08)" strokeWidth="0.5">
          {[...Array(13)].map((_, i) => (
            <line key={`v${i}`} x1={(i * W) / 12} y1={0} x2={(i * W) / 12} y2={H} />
          ))}
          {[...Array(7)].map((_, i) => (
            <line key={`h${i}`} x1={0} y1={(i * H) / 6} x2={W} y2={(i * H) / 6} />
          ))}
        </g>
        {/* equator */}
        <line x1={0} y1={H / 2} x2={W} y2={H / 2} stroke="rgba(200,162,75,0.18)" strokeDasharray="4 4" />
        {/* prime meridian */}
        <line x1={W / 2} y1={0} x2={W / 2} y2={H} stroke="rgba(200,162,75,0.18)" strokeDasharray="4 4" />

        {/* corner gold marks */}
        {[
          [10, 10, "tl"],
          [W - 10, 10, "tr"],
          [10, H - 10, "bl"],
          [W - 10, H - 10, "br"],
        ].map(([cx, cy, k]) => (
          <g key={k} stroke="#c8a24b" strokeWidth="1">
            <line x1={cx - 6} y1={cy} x2={cx + 6} y2={cy} />
            <line x1={cx} y1={cy - 6} x2={cx} y2={cy + 6} />
          </g>
        ))}

        {/* threat origin nodes */}
        {nodes.map((n) => {
          const { x, y } = projectGeo(n.lat, n.lon, W, H);
          const r = 4 + Math.sqrt(n.count / max) * 18;
          const color = n.critical > 0 ? "#ff7a6e" : "#dcb95f";
          return (
            <g key={n.country} data-testid={`geo-node-${n.country}`}>
              {/* pulse */}
              <circle cx={x} cy={y} r={r + 4} fill={color} opacity="0.08">
                <animate attributeName="r" values={`${r};${r + 12};${r}`} dur="2.4s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.16;0;0.16" dur="2.4s" repeatCount="indefinite" />
              </circle>
              <circle cx={x} cy={y} r={r} fill={color} opacity="0.25" />
              <circle cx={x} cy={y} r={3} fill={color} stroke="#0c0c11" strokeWidth="0.8" />
              <text
                x={x + r + 6}
                y={y + 3}
                fill="#ece4cf"
                fontFamily="JetBrains Mono"
                fontSize="9"
                letterSpacing="0.1em"
              >
                {n.country} · {n.count}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="absolute bottom-3 left-3 font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">
        Equirectangular · Live Threat Origins
      </div>
      <div className="absolute top-3 right-3 flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: "#ff7a6e" }} />
          <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-ivory-2">critical present</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: "#dcb95f" }} />
          <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-ivory-2">activity</span>
        </div>
      </div>
    </div>
  );
};

const Heatmaps = () => {
  const [geo, setGeo] = useState([]);
  const [mitre, setMitre] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [g, m, a] = await Promise.all([
          api.get("/heatmaps/geo"),
          api.get("/heatmaps/mitre"),
          api.get("/heatmaps/asset-risk"),
        ]);
        setGeo(g.data.nodes);
        setMitre(m.data.matrix);
        setAssets(a.data.assets);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const sortedGeo = useMemo(() => [...geo].sort((a, b) => b.count - a.count), [geo]);

  if (loading) {
    return <div className="text-center py-20" data-testid="heatmaps-loading"><span className="dot-loader"><span /><span /><span /></span></div>;
  }

  return (
    <div className="space-y-6" data-testid="heatmaps">
      <SectionTitle sub="// CARTOGRAPHIES OF AGGRESSION">
        Heatmaps
      </SectionTitle>

      <Panel
        testid="panel-geo"
        title="Geo-Origin Threat Map"
        code="§ III.A"
        action={
          <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-ivory-3 flex items-center gap-2">
            <Globe2 size={11} className="text-gold-300" />
            {geo.length} regions
          </span>
        }
      >
        <div className="p-4">
          <GeoHeatmap nodes={geo} />
        </div>
        <div className="px-5 pb-5 border-t border-[color:var(--gold-line)] pt-3">
          <div className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase mb-2">
            Top Adversarial Regions
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="geo-rank">
            {sortedGeo.slice(0, 6).map((g) => (
              <div key={g.country} className="border border-[color:var(--gold-line)] p-2">
                <div className="font-display text-[14px] text-gold-100">{g.country}</div>
                <div className="font-mono text-[9px] text-ivory-2 truncate">{g.country_name}</div>
                <div className="font-mono text-[10px] text-gold-200 mt-1">{g.count} events</div>
                {g.critical > 0 && <div className="font-mono text-[9px]" style={{ color: "#ff7a6e" }}>{g.critical} critical</div>}
              </div>
            ))}
          </div>
        </div>
      </Panel>

      <Panel
        testid="panel-mitre"
        title="MITRE ATT&CK Matrix"
        code="§ III.B"
        action={
          <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-ivory-3 flex items-center gap-2">
            <Grid3x3 size={11} className="text-gold-300" />
            12 tactics · 48 techniques
          </span>
        }
      >
        <div className="p-5 overflow-x-auto">
          <table className="w-full" style={{ tableLayout: "fixed", minWidth: 1100 }} data-testid="mitre-matrix">
            <thead>
              <tr>
                {mitre.map((tac) => (
                  <th
                    key={tac.tactic}
                    className="px-1 pb-3 font-mono text-[9px] tracking-[0.12em] text-gold-200 uppercase text-center"
                    style={{ borderBottom: "1px solid var(--gold-line)" }}
                  >
                    {tac.tactic}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[0, 1, 2, 3].map((rowIdx) => (
                <tr key={rowIdx}>
                  {mitre.map((tac) => {
                    const t = tac.techniques[rowIdx];
                    if (!t) return <td key={tac.tactic} />;
                    return (
                      <td key={tac.tactic + t.technique} className="p-0.5">
                        <div
                          className="mitre-cell"
                          style={mitreCellStyle(t.count, t.critical)}
                          title={`${tac.tactic} → ${t.technique} · ${t.count} events`}
                          data-testid={`mitre-${t.technique}`}
                        >
                          <div className="font-display text-[11px] tracking-[0.04em]">{t.technique}</div>
                          <div className="text-[9px] mt-0.5 opacity-80">{t.count}</div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel
        testid="panel-asset-risk"
        title="Asset Risk Heatmap"
        code="§ III.C"
        action={
          <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-ivory-3 flex items-center gap-2">
            <Server size={11} className="text-gold-300" />
            {assets.length} assets
          </span>
        }
      >
        <div className="p-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="asset-grid">
          {assets.map((a) => {
            const risk = a.risk_score;
            const color =
              risk >= 80 ? "#ff7a6e" :
              risk >= 60 ? "#f5a73a" :
              risk >= 40 ? "#dcb95f" :
                            "#9bb1c8";
            return (
              <div
                key={a.id}
                className="border p-3 transition-all hover:scale-[1.02]"
                style={{
                  borderColor: `${color}55`,
                  background: `linear-gradient(140deg, ${color}10, transparent 60%)`,
                }}
                data-testid={`asset-${a.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="font-mono text-[10px] tracking-[0.14em] text-gold-100 truncate">
                    {a.name}
                  </div>
                  <span
                    className="font-display text-[14px]"
                    style={{ color, textShadow: `0 0 12px ${color}55` }}
                  >
                    {risk}
                  </span>
                </div>
                <div className="font-mono text-[9px] text-ivory-3 tracking-[0.1em] uppercase mb-2">{a.type}</div>
                <div className="h-1 w-full bg-[var(--obsidian-3)] border border-[color:var(--gold-line)]">
                  <div
                    className="h-full"
                    style={{ width: `${risk}%`, background: color, boxShadow: `0 0 6px ${color}` }}
                  />
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className={`status-pill status-${a.criticality === "critical" ? "open" : "active"}`}>
                    {a.criticality}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </Panel>
    </div>
  );
};

export default Heatmaps;
